import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

/**
 * Estrategia de precarga personalizada que mejora el rendimiento
 * y evita problemas de carga de módulos dinámicos
 */
@Injectable({
  providedIn: 'root',
})
export class CustomPreloadingStrategy implements PreloadingStrategy {
  // Módulos críticos que se precargan inmediatamente
  private criticalModules = ['/home', '/clients', '/vehicle'];

  // Módulos que se precargan con retraso
  private delayedModules = [
    '/claims',
    '/diagnosis',
    '/quotes',
    '/appointments',
    '/users',
    '/insurance',
    '/suppliers',
    '/company',
    '/items',
    '/parts',
    '/deliverynotes',
    '/purchaseorders',
    '/purchase-inventory',
    '/returns',
    '/settings',
  ];

  // Contador de reintentos para manejar errores
  private retryAttempts = new Map<string, number>();
  private maxRetries = 3;

  /**
   * Determina si un módulo debe ser precargado y cómo
   * @param route Ruta a evaluar
   * @param fn Función para cargar el módulo
   * @returns Observable que gestiona la precarga
   */
  preload(route: Route, fn: () => Observable<any>): Observable<any> {
    const routePath = route.path || '';

    // No precargar rutas de autenticación
    if (this.isAuthRoute(routePath)) {
      return of(null);
    }

    // Precarga inmediata para módulos críticos
    if (this.isCriticalModule(routePath)) {
      return this.preloadWithRetry(routePath, fn);
    }

    // Precarga con retraso para módulos secundarios
    if (this.isDelayedModule(routePath)) {
      return timer(2000).pipe(
        switchMap(() => this.preloadWithRetry(routePath, fn)),
        catchError((error) => {
          console.warn(`Error precargando módulo ${routePath}:`, error);
          return of(null);
        })
      );
    }

    // No precargar otros módulos
    return of(null);
  }

  /**
   * Precarga un módulo con reintentos en caso de error
   * @param routePath Ruta del módulo
   * @param fn Función para cargar el módulo
   * @returns Observable que maneja la precarga con reintentos
   */
  private preloadWithRetry(
    routePath: string,
    fn: () => Observable<any>
  ): Observable<any> {
    const currentAttempts = this.retryAttempts.get(routePath) || 0;

    return fn().pipe(
      catchError((error) => {
        if (currentAttempts < this.maxRetries) {
          this.retryAttempts.set(routePath, currentAttempts + 1);
          console.warn(
            `Error precargando ${routePath}, reintentando... (${
              currentAttempts + 1
            }/${this.maxRetries})`,
            error
          );

          // Reintentar con retraso exponencial
          const delay = Math.pow(2, currentAttempts) * 1000;
          return timer(delay).pipe(
            switchMap(() => this.preloadWithRetry(routePath, fn))
          );
        } else {
          console.error(
            `Error definitivo precargando ${routePath} después de ${this.maxRetries} intentos:`,
            error
          );
          this.retryAttempts.delete(routePath);
          return of(null);
        }
      })
    );
  }

  /**
   * Verifica si una ruta es de autenticación
   * @param routePath Ruta a verificar
   * @returns true si es ruta de autenticación
   */
  private isAuthRoute(routePath: string): boolean {
    return (
      routePath.includes('login') || routePath.includes('recover-password')
    );
  }

  /**
   * Verifica si un módulo es crítico
   * @param routePath Ruta a verificar
   * @returns true si es módulo crítico
   */
  private isCriticalModule(routePath: string): boolean {
    return this.criticalModules.some(
      (module) =>
        routePath.includes(module.substring(1)) ||
        routePath === module.substring(1)
    );
  }

  /**
   * Verifica si un módulo debe precargarse con retraso
   * @param routePath Ruta a verificar
   * @returns true si debe precargarse con retraso
   */
  private isDelayedModule(routePath: string): boolean {
    return this.delayedModules.some(
      (module) =>
        routePath.includes(module.substring(1)) ||
        routePath === module.substring(1)
    );
  }

  /**
   * Resetea los contadores de reintentos
   */
  resetRetryCounters(): void {
    this.retryAttempts.clear();
  }

  /**
   * Obtiene estadísticas de precarga
   * @returns Objeto con estadísticas
   */
  getStats(): { totalRetries: number; failedModules: string[] } {
    const totalRetries = Array.from(this.retryAttempts.values()).reduce(
      (sum, count) => sum + count,
      0
    );
    const failedModules = Array.from(this.retryAttempts.keys());

    return {
      totalRetries,
      failedModules,
    };
  }
}
